import { useState, useEffect } from "react";
import { Label } from "../shadcn/ui/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../shadcn/ui/Select";
import { Checkbox } from "../shadcn/ui/Checkbox";
import { Card, CardContent } from "../shadcn/ui/Card";

interface CategoryHierarchy {
  id: string;
  name: string;
  slug: string;
  parentId?: string;
  level?: number;
  children?: CategoryHierarchy[];
  productCount?: number;
}

interface ThreeLevelCategorySelectorProps {
  categories: CategoryHierarchy[];
  selectedCategoryIds: string[];
  onChange: (categoryIds: string[]) => void;
  label?: string;
  required?: boolean;
  allowMultiSelect?: boolean;
}

export const ThreeLevelCategorySelector = ({
  categories,
  selectedCategoryIds = [],
  onChange,
  label = "Categories",
  required = false,
  allowMultiSelect = true,
}: ThreeLevelCategorySelectorProps) => {
  const [selectedLevel0Id, setSelectedLevel0Id] = useState<string>("");
  const [selectedLevel1Id, setSelectedLevel1Id] = useState<string>("");
  const [selectedLevel2Id, setSelectedLevel2Id] = useState<string>("");

  // Get root categories (level 0)
  const getRootCategories = (): CategoryHierarchy[] => {
    return categories.filter((cat) => !cat.parentId);
  };

  // Get level 1 children of a level 0 category
  const getLevel1Children = (parentId: string): CategoryHierarchy[] => {
    const parent = categories.find((cat) => cat.id === parentId);
    return parent?.children || [];
  };

  // Get level 2 children of a level 1 category
  const getLevel2Children = (parentId: string): CategoryHierarchy[] => {
    for (const rootCat of categories) {
      if (rootCat.children) {
        const level1Cat = rootCat.children.find((cat) => cat.id === parentId);
        if (level1Cat?.children) {
          return level1Cat.children;
        }
      }
    }
    return [];
  };

  // Handle level 0 selection
  const handleLevel0Change = (value: string) => {
    setSelectedLevel0Id(value);
    setSelectedLevel1Id("");
    setSelectedLevel2Id("");

    if (value && !allowMultiSelect) {
      // Single select mode - select the category immediately
      onChange([value]);
    } else if (value && allowMultiSelect) {
      // Multi-select mode - add to selection if not already selected
      if (!selectedCategoryIds.includes(value)) {
        onChange([...selectedCategoryIds, value]);
      }
    }
  };

  // Handle level 1 selection
  const handleLevel1Change = (value: string) => {
    setSelectedLevel1Id(value);
    setSelectedLevel2Id("");

    if (value === "none") {
      // User wants to use parent (level 0) only
      if (selectedLevel0Id && !selectedCategoryIds.includes(selectedLevel0Id)) {
        onChange([...selectedCategoryIds, selectedLevel0Id]);
      }
      return;
    }

    if (value && !allowMultiSelect) {
      onChange([value]);
    } else if (value && allowMultiSelect) {
      // Remove parent if it was selected, add level 1
      const newIds = selectedCategoryIds.filter((id) => id !== selectedLevel0Id);
      if (!newIds.includes(value)) {
        onChange([...newIds, value]);
      } else {
        onChange(newIds);
      }
    }
  };

  // Handle level 2 selection
  const handleLevel2Change = (value: string) => {
    setSelectedLevel2Id(value);

    if (value === "none") {
      // User wants to use level 1 only
      if (selectedLevel1Id && !selectedCategoryIds.includes(selectedLevel1Id)) {
        const newIds = selectedCategoryIds.filter((id) => id !== selectedLevel0Id);
        onChange([...newIds, selectedLevel1Id]);
      }
      return;
    }

    if (value && !allowMultiSelect) {
      onChange([value]);
    } else if (value && allowMultiSelect) {
      // Remove parent and level 1 if they were selected, add level 2
      const newIds = selectedCategoryIds.filter(
        (id) => id !== selectedLevel0Id && id !== selectedLevel1Id
      );
      if (!newIds.includes(value)) {
        onChange([...newIds, value]);
      } else {
        onChange(newIds);
      }
    }
  };

  // Checkbox handler for multi-select mode
  const handleCategoryToggle = (categoryId: string, checked: boolean) => {
    if (checked) {
      if (!selectedCategoryIds.includes(categoryId)) {
        onChange([...selectedCategoryIds, categoryId]);
      }
    } else {
      onChange(selectedCategoryIds.filter((id) => id !== categoryId));
    }
  };

  const level1Children = selectedLevel0Id ? getLevel1Children(selectedLevel0Id) : [];
  const level2Children = selectedLevel1Id ? getLevel2Children(selectedLevel1Id) : [];

  // Flatten all categories for checkbox display
  const getAllSelectableCategories = (): Array<{ id: string; name: string; level: number; path: string }> => {
    const result: Array<{ id: string; name: string; level: number; path: string }> = [];
    
    categories.forEach((level0) => {
      result.push({ id: level0.id, name: level0.name, level: 0, path: level0.name });
      
      if (level0.children) {
        level0.children.forEach((level1) => {
          result.push({ 
            id: level1.id, 
            name: level1.name, 
            level: 1, 
            path: `${level0.name} > ${level1.name}` 
          });
          
          if (level1.children) {
            level1.children.forEach((level2) => {
              result.push({ 
                id: level2.id, 
                name: level2.name, 
                level: 2, 
                path: `${level0.name} > ${level1.name} > ${level2.name}` 
              });
            });
          }
        });
      }
    });
    
    return result;
  };

  return (
    <div className="space-y-4">
      <Label>
        {label} {required && "*"}
      </Label>

      {allowMultiSelect ? (
        // Multi-select mode: Show checkboxes for all categories with hierarchy display
        <Card>
          <CardContent className="p-4">
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {categories.map((level0) => (
                <div key={level0.id} className="space-y-2">
                  {/* Level 0 */}
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`cat-${level0.id}`}
                      checked={selectedCategoryIds.includes(level0.id)}
                      onCheckedChange={(checked) =>
                        handleCategoryToggle(level0.id, checked as boolean)
                      }
                    />
                    <label
                      htmlFor={`cat-${level0.id}`}
                      className="text-sm font-medium cursor-pointer flex-1"
                    >
                      {level0.name}
                    </label>
                  </div>

                  {/* Level 1 */}
                  {level0.children && level0.children.length > 0 && (
                    <div className="ml-6 space-y-2">
                      {level0.children.map((level1) => (
                        <div key={level1.id} className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id={`cat-${level1.id}`}
                              checked={selectedCategoryIds.includes(level1.id)}
                              onCheckedChange={(checked) =>
                                handleCategoryToggle(level1.id, checked as boolean)
                              }
                            />
                            <label
                              htmlFor={`cat-${level1.id}`}
                              className="text-sm cursor-pointer flex-1"
                            >
                              {level1.name}
                            </label>
                          </div>

                          {/* Level 2 */}
                          {level1.children && level1.children.length > 0 && (
                            <div className="ml-6 space-y-1">
                              {level1.children.map((level2) => (
                                <div key={level2.id} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`cat-${level2.id}`}
                                    checked={selectedCategoryIds.includes(level2.id)}
                                    onCheckedChange={(checked) =>
                                      handleCategoryToggle(level2.id, checked as boolean)
                                    }
                                  />
                                  <label
                                    htmlFor={`cat-${level2.id}`}
                                    className="text-xs text-muted-foreground cursor-pointer flex-1"
                                  >
                                    {level2.name}
                                  </label>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        // Single select mode: Show cascading dropdowns
        <div className="space-y-4">
          {/* Level 0 Dropdown */}
          <Select value={selectedLevel0Id} onValueChange={handleLevel0Change}>
            <SelectTrigger>
              <SelectValue placeholder="Select category (Level 0)" />
            </SelectTrigger>
            <SelectContent>
              {getRootCategories().map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Level 1 Dropdown */}
          {level1Children.length > 0 && (
            <Select value={selectedLevel1Id} onValueChange={handleLevel1Change}>
              <SelectTrigger>
                <SelectValue placeholder="Select subcategory (Level 1) - Optional" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Use Parent Category Only</SelectItem>
                {level1Children.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Level 2 Dropdown */}
          {level2Children.length > 0 && (
            <Select value={selectedLevel2Id} onValueChange={handleLevel2Change}>
              <SelectTrigger>
                <SelectValue placeholder="Select subcategory (Level 2) - Optional" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Use Level 1 Category Only</SelectItem>
                {level2Children.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {/* Show selected categories */}
      {selectedCategoryIds.length > 0 && (
        <div className="text-sm text-muted-foreground">
          Selected: {selectedCategoryIds.length} categor{selectedCategoryIds.length === 1 ? "y" : "ies"}
        </div>
      )}
    </div>
  );
};

export default ThreeLevelCategorySelector;

